import React, { useState } from 'react';
import { Search, Plus, Edit2, Trash2 } from 'lucide-react';
import { seoService } from '../../../services/seoService';

const KeywordManagementTable = ({ keywords, onRefresh, fullView = false }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState(null);
  const [formData, setFormData] = useState({
    keyword: '',
    searchVolume: 0,
    difficulty: 'medium',
    currentRanking: null,
    targetRanking: null,
    targetUrl: '',
    category: 'general',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      if (editingKeyword) {
        await seoService?.updateKeyword(editingKeyword?.id, formData);
      } else {
        await seoService?.createKeyword(formData);
      }
      setShowAddModal(false);
      setEditingKeyword(null);
      resetForm();
      onRefresh?.();
    } catch (error) {
      console.error('Error saving keyword:', error);
      alert('Erreur lors de l\'enregistrement du mot-clé');
    }
  };

  const handleEdit = (keyword) => {
    setEditingKeyword(keyword);
    setFormData({
      keyword: keyword?.keyword,
      searchVolume: keyword?.search_volume,
      difficulty: keyword?.difficulty,
      currentRanking: keyword?.current_ranking,
      targetRanking: keyword?.target_ranking,
      targetUrl: keyword?.target_url,
      category: keyword?.category,
      notes: keyword?.notes || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (keywordId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce mot-clé ?')) return;
    try {
      await seoService?.deleteKeyword(keywordId);
      onRefresh?.();
    } catch (error) {
      console.error('Error deleting keyword:', error);
      alert('Erreur lors de la suppression du mot-clé');
    }
  };

  const resetForm = () => {
    setFormData({
      keyword: '',
      searchVolume: 0,
      difficulty: 'medium',
      currentRanking: null,
      targetRanking: null,
      targetUrl: '',
      category: 'general',
      notes: ''
    });
  };

  const displayKeywords = fullView ? keywords : keywords?.slice(0, 5);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Search className="w-6 h-6 text-green-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Gestion des mots-clés</h3>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingKeyword(null);
            setShowAddModal(true);
          }}
          className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
        >
          <Plus size={16} />
          <span>Ajouter</span>
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Mot-clé</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Volume</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Difficulté</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Position</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Cible</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {displayKeywords?.map((keyword) => (
              <tr key={keyword?.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900 font-medium">{keyword?.keyword}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{keyword?.search_volume?.toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    keyword?.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                    keyword?.difficulty === 'medium'? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {keyword?.difficulty === 'easy' ? 'Facile' : keyword?.difficulty === 'medium' ? 'Moyen' : 'Difficile'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {keyword?.current_ranking ? `#${keyword?.current_ranking}` : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {keyword?.target_ranking ? `#${keyword?.target_ranking}` : '-'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(keyword)}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(keyword?.id)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {editingKeyword ? 'Modifier le mot-clé' : 'Ajouter un mot-clé'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mot-clé *</label>
                    <input
                      type="text"
                      required
                      value={formData?.keyword}
                      onChange={(e) => setFormData({ ...formData, keyword: e?.target?.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Volume de recherche</label>
                    <input
                      type="number"
                      value={formData?.searchVolume}
                      onChange={(e) => setFormData({ ...formData, searchVolume: parseInt(e?.target?.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Difficulté</label>
                    <select
                      value={formData?.difficulty}
                      onChange={(e) => setFormData({ ...formData, difficulty: e?.target?.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    >
                      <option value="easy">Facile</option>
                      <option value="medium">Moyen</option>
                      <option value="hard">Difficile</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                    <select
                      value={formData?.category}
                      onChange={(e) => setFormData({ ...formData, category: e?.target?.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    >
                      <option value="general">Général</option>
                      <option value="shipping">Envoi</option>
                      <option value="logistics">Logistique</option>
                      <option value="pricing">Tarifs</option>
                      <option value="brand">Marque</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Position actuelle</label>
                    <input
                      type="number"
                      value={formData?.currentRanking || ''}
                      onChange={(e) => setFormData({ ...formData, currentRanking: parseInt(e?.target?.value) || null })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Position cible</label>
                    <input
                      type="number"
                      value={formData?.targetRanking || ''}
                      onChange={(e) => setFormData({ ...formData, targetRanking: parseInt(e?.target?.value) || null })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL cible</label>
                  <input
                    type="text"
                    value={formData?.targetUrl}
                    onChange={(e) => setFormData({ ...formData, targetUrl: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    placeholder="/shipping-information"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData?.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingKeyword(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    {editingKeyword ? 'Mettre à jour' : 'Ajouter'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeywordManagementTable;